import { isDrivable, latLngToLocalMeters, roadWidthMeters } from '@roadworld/map-generator';
import type { ChunkPayload, OsmWay, RoadClass } from '@roadworld/shared';
import type { HeightfieldData, MeshGeometry } from './geometry.js';
import { sampleHeightfield } from './heightfield.js';

/**
 * Stripes run ALONG the driving direction (each stripe is a long bar parallel to the
 * road centerline), with multiple stripes arranged side-by-side across the road.
 */
const STRIPE_COUNT = 5;
const STRIPE_WIDTH_M = 0.5;
const STRIPE_GAP_M = 0.5;
const STRIPE_HALF_LENGTH_M = 2.5;
const LIFT_ABOVE_ROAD_M = 0.15;
const ROAD_EDGE_INSET = 0.85;

const ROAD_PRIORITY: Record<string, number> = {
  motorway: 10,
  trunk: 9,
  primary: 8,
  secondary: 7,
  tertiary: 6,
  residential: 5,
  unclassified: 4,
  service: 3,
  track: 2,
};

interface Placement {
  way: OsmWay;
  index: number;
  isInterior: boolean;
}

export function buildCrossingMeshes(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
): MeshGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];

  if (payload.osm.crossings.length === 0) return emptyGeom();

  const crossingIds = new Set(payload.osm.crossings.map((c) => c.id));

  const placements = new Map<number, Placement>();
  for (const way of payload.osm.ways) {
    if (!isDrivable(way.roadClass)) continue;
    for (let i = 0; i < way.nodes.length; i++) {
      const nodeId = way.nodes[i]!;
      if (!crossingIds.has(nodeId)) continue;
      const isInterior = i > 0 && i < way.nodes.length - 1;
      const existing = placements.get(nodeId);
      if (!existing || better({ way, index: i, isInterior }, existing)) {
        placements.set(nodeId, { way, index: i, isInterior });
      }
    }
  }

  for (const p of placements.values()) {
    const { way, index: i } = p;
    const nodeId = way.nodes[i]!;
    const center = payload.osm.nodes[nodeId];
    if (!center) continue;

    const prevId = way.nodes[Math.max(0, i - 1)]!;
    const nextId = way.nodes[Math.min(way.nodes.length - 1, i + 1)]!;
    const prev = payload.osm.nodes[prevId];
    const next = payload.osm.nodes[nextId];
    if (!prev || !next || prevId === nextId) continue;

    const c = latLngToLocalMeters(center.lat, center.lng, payload.origin.lat, payload.origin.lng);
    const a = latLngToLocalMeters(prev.lat, prev.lng, payload.origin.lat, payload.origin.lng);
    const b = latLngToLocalMeters(next.lat, next.lng, payload.origin.lat, payload.origin.lng);

    // Tangent — unit-vector average of forward + backward segments.
    const dx1 = c.x - a.x;
    const dz1 = c.z - a.z;
    const dx2 = b.x - c.x;
    const dz2 = b.z - c.z;
    const l1 = Math.hypot(dx1, dz1) || 1;
    const l2 = Math.hypot(dx2, dz2) || 1;
    let alongX = dx1 / l1 + dx2 / l2;
    let alongZ = dz1 / l1 + dz2 / l2;
    const aLen = Math.hypot(alongX, alongZ) || 1;
    alongX /= aLen;
    alongZ /= aLen;
    const acrossX = -alongZ;
    const acrossZ = alongX;

    const roadWidth = roadWidthMeters(way.roadClass);
    const naturalSpread = STRIPE_COUNT * STRIPE_WIDTH_M + (STRIPE_COUNT - 1) * STRIPE_GAP_M;
    const maxSpread = roadWidth * ROAD_EDGE_INSET;
    const spreadScale = Math.min(1, maxSpread / naturalSpread);
    const stripeWidth = STRIPE_WIDTH_M * spreadScale;
    const stripeGap = STRIPE_GAP_M * spreadScale;

    appendStripes(
      c.x,
      c.z,
      heightfield,
      alongX,
      alongZ,
      acrossX,
      acrossZ,
      STRIPE_HALF_LENGTH_M,
      stripeWidth,
      stripeGap,
      positions,
      indices,
      normals,
      uvs,
    );
  }

  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
    normals: new Float32Array(normals),
    uvs: new Float32Array(uvs),
  };
}

function better(candidate: Placement, current: Placement): boolean {
  if (candidate.isInterior && !current.isInterior) return true;
  if (!candidate.isInterior && current.isInterior) return false;
  return roadPriority(candidate.way.roadClass) > roadPriority(current.way.roadClass);
}

function roadPriority(rc: RoadClass): number {
  return ROAD_PRIORITY[rc] ?? 0;
}

function appendStripes(
  cx: number,
  cz: number,
  heightfield: HeightfieldData,
  alongX: number,
  alongZ: number,
  acrossX: number,
  acrossZ: number,
  stripeHalfLength: number,
  stripeWidth: number,
  stripeGap: number,
  positions: number[],
  indices: number[],
  normals: number[],
  uvs: number[],
): void {
  const totalSpread = STRIPE_COUNT * stripeWidth + (STRIPE_COUNT - 1) * stripeGap;
  for (let s = 0; s < STRIPE_COUNT; s++) {
    const acrossOffset = -totalSpread * 0.5 + s * (stripeWidth + stripeGap) + stripeWidth * 0.5;
    const sCx = cx + acrossX * acrossOffset;
    const sCz = cz + acrossZ * acrossOffset;
    const halfW = stripeWidth * 0.5;

    const c1x = sCx - alongX * stripeHalfLength - acrossX * halfW;
    const c1z = sCz - alongZ * stripeHalfLength - acrossZ * halfW;
    const c2x = sCx - alongX * stripeHalfLength + acrossX * halfW;
    const c2z = sCz - alongZ * stripeHalfLength + acrossZ * halfW;
    const c3x = sCx + alongX * stripeHalfLength + acrossX * halfW;
    const c3z = sCz + alongZ * stripeHalfLength + acrossZ * halfW;
    const c4x = sCx + alongX * stripeHalfLength - acrossX * halfW;
    const c4z = sCz + alongZ * stripeHalfLength - acrossZ * halfW;

    // Sample terrain at each corner so the stripe banks with the slope, matching how
    // road and path ribbons follow terrain. Otherwise on cross-slopes a flat stripe
    // can dip below the road's banked edge and disappear.
    const c1y = sampleHeightfield(heightfield, c1x, c1z) + LIFT_ABOVE_ROAD_M;
    const c2y = sampleHeightfield(heightfield, c2x, c2z) + LIFT_ABOVE_ROAD_M;
    const c3y = sampleHeightfield(heightfield, c3x, c3z) + LIFT_ABOVE_ROAD_M;
    const c4y = sampleHeightfield(heightfield, c4x, c4z) + LIFT_ABOVE_ROAD_M;

    const base = positions.length / 3;
    positions.push(c1x, c1y, c1z, c2x, c2y, c2z, c3x, c3y, c3z, c4x, c4y, c4z);
    normals.push(0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0);
    uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
    indices.push(base, base + 2, base + 1, base, base + 3, base + 2);
  }
}

function emptyGeom(): MeshGeometry {
  return {
    positions: new Float32Array(0),
    indices: new Uint32Array(0),
    normals: new Float32Array(0),
    uvs: new Float32Array(0),
  };
}
